import { inject, Injectable } from '@angular/core';

import { type AggregateFunction } from '@blaze-shared/api-contracts-domain';
import {
  type AggregateFunctionWithGroup,
  IntelliSenseService,
} from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import {
  epochToDateTime,
  isDateOrDateTimeString,
  maxDateString,
  minDateString,
  toEpoch,
} from '@blaze-shared/shared-util';
import { type AggregateWorkflowNode, getDefaultFunctionAlias } from '@blaze-shared/workflow-domain';

import { normalizeNodeInput } from '../../node-input-rows';

/** Rows collected per group-by key, one entry per group. */
type AggregatedGroups = { [groupByKey: string]: DatasetRow };

/**
 * Collapses a node's input rows into one row per group.
 *
 * A node declares its aggregation in one of three ways, which is what the three branches below
 * distinguish: typed function definitions (what the editor writes today), function expressions
 * (what older workflows carry), or group-by columns alone — which makes the node a `distinct`.
 */
@Injectable({ providedIn: 'root' })
export class AggregateActionService {
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  aggregate(node: AggregateWorkflowNode, nodeInput: DatasetRow[]): DatasetRow[] {
    const rows = normalizeNodeInput(nodeInput);
    const functionsExpr = node.functions;
    let groupByColumns = node.groupByColumns;

    let aggFns: AggregateFunctionWithGroup[] = [];
    if (node.functionDefs?.length) {
      aggFns = node.functionDefs.map((fnDef) => ({
        ...fnDef,
        alias: this.getAggregateColumnAlias(fnDef),
        column: this.schemaService.getColumnName(fnDef.column) || fnDef.column,
        groupBy: node.groupByColumns.map(
          (column) => this.schemaService.getColumnName(column) || column,
        ),
      }));
    } else if (node.functions?.length && functionsExpr?.length) {
      aggFns = this.iss.createAggregateFunctions(functionsExpr, groupByColumns);
    } else if (!node.functions?.length && !functionsExpr?.length && node.groupByColumns?.length) {
      groupByColumns = groupByColumns.map(
        (column) => this.schemaService.getColumnName(column) || column,
      );
    }

    let aggregate: AggregatedGroups = {};
    if (aggFns?.length) {
      aggFns.forEach((fn) => {
        aggregate = this.applyAggregateFunction(fn, rows, aggregate);
      });
    } else {
      aggregate = this.getDistinct(rows, groupByColumns, aggregate);
    }

    return Object.values(aggregate).sort();
  }

  private applyAggregateFunction(
    fn: AggregateFunctionWithGroup,
    rows: DatasetRow[],
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    switch (fn.name?.toLowerCase()) {
      case 'array':
        return this.getArrayFromColumn(rows, fn, aggregate);
      case 'avg':
        return this.getAverageFromColumn(rows, fn, aggregate);
      case 'count':
        return this.getCountFromColumn(rows, fn, aggregate);
      case 'max':
        return this.getMaxFromColumn(rows, fn, aggregate);
      case 'min':
        return this.getMinFromColumn(rows, fn, aggregate);
      case 'sum':
        return this.getSumFromColumn(rows, fn, aggregate);
      default:
        return aggregate;
    }
  }

  private getAggregateColumnAlias(fn: AggregateFunction): string {
    if (fn.alias) {
      return fn.alias;
    }
    return this.schemaService.replaceColumnIdsWithNames(
      getDefaultFunctionAlias(fn.name, fn.column),
    );
  }

  private getArrayFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const aggregateCol = fn.alias || `${fn.column}`;
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      aggregateRow[aggregateCol] = aggregateRow[aggregateCol] || [];
      if (row[fn.column]) {
        aggregateRow[aggregateCol].push(row[fn.column]);
      }
      aggregate[groupByKey] = aggregateRow;
    });
    return aggregate;
  }

  private getAverageFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const aggregateCol = fn.alias || `${fn.name}_${fn.column}`;
    const valuesPerGroup: { [groupByKey: string]: number } = {};

    // Sum first, divide once the group is complete — the count is only known at the end.
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      valuesPerGroup[groupByKey] = (valuesPerGroup[groupByKey] ?? 0) + 1;
      aggregateRow[aggregateCol] = (aggregateRow[aggregateCol] || 0) + row[fn.column];
      aggregate[groupByKey] = aggregateRow;
    });

    Object.entries(aggregate).forEach(([groupByKey, aggregateRow]) => {
      aggregate[groupByKey][aggregateCol] = aggregateRow[aggregateCol] / valuesPerGroup[groupByKey];
    });

    return aggregate;
  }

  private getCountFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const aggregateCol = fn.alias || `${fn.name}`;
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      aggregateRow[aggregateCol] = (aggregateRow[aggregateCol] || 0) + 1;
      aggregate[groupByKey] = aggregateRow;
    });
    return aggregate;
  }

  private getDistinct(
    rows: DatasetRow[],
    groupByColumns: string[],
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    rows.forEach((row) => {
      aggregate[createGroupByKey(row, groupByColumns)] = createGroupRow(row, groupByColumns);
    });
    return aggregate;
  }

  private getMaxFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const maxDate = epochToDateTime(0);
    const aggregateCol = fn.alias || `${fn.name}_${fn.column}`;
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      const colValue = row[fn.column];
      if (isDateOrDateTimeString(colValue)) {
        aggregateRow[aggregateCol] = aggregateRow[aggregateCol] || maxDate;
        const epoch = toEpoch(aggregateRow[aggregateCol]);
        aggregateRow[aggregateCol] = maxDateString(colValue, epoch);
      } else {
        aggregateRow[aggregateCol] = Math.max(
          isNaN(aggregateRow[aggregateCol]) ? Number.MIN_SAFE_INTEGER : aggregateRow[aggregateCol],
          colValue,
        );
      }
      aggregate[groupByKey] = aggregateRow;
    });
    return aggregate;
  }

  private getMinFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const minDate = epochToDateTime(99999999999999);
    const aggregateCol = fn.alias || `${fn.name}_${fn.column}`;
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      const colValue = row[fn.column];
      if (isDateOrDateTimeString(colValue)) {
        aggregateRow[aggregateCol] = aggregateRow[aggregateCol] || minDate;
        const epoch = toEpoch(aggregateRow[aggregateCol]);
        aggregateRow[aggregateCol] = minDateString(colValue, epoch);
      } else {
        aggregateRow[aggregateCol] = Math.min(
          isNaN(aggregateRow[aggregateCol]) ? Number.MAX_SAFE_INTEGER : aggregateRow[aggregateCol],
          colValue,
        );
      }
      aggregate[groupByKey] = aggregateRow;
    });
    return aggregate;
  }

  private getSumFromColumn(
    rows: DatasetRow[],
    fn: AggregateFunctionWithGroup,
    aggregate: AggregatedGroups,
  ): AggregatedGroups {
    const aggregateCol = fn.alias || `${fn.name}_${fn.column}`;
    rows.forEach((row) => {
      const groupByKey = createGroupByKey(row, fn.groupBy);
      const aggregateRow = aggregate[groupByKey] || createGroupRow(row, fn.groupBy);
      aggregateRow[aggregateCol] = (aggregateRow[aggregateCol] || 0) + row[fn.column];
      aggregate[groupByKey] = aggregateRow;
    });
    return aggregate;
  }
}

/** Identifies the group a row belongs to; a linked value groups by the row it links to. */
function createGroupByKey(row: DatasetRow, cols: string[]): string {
  return cols
    .filter((col) => !!col)
    .reduce((key, col) => {
      const value = row[col];
      return key + (value?.['rowId'] ?? value);
    }, '');
}

/** Seeds a group's row with the values it is grouped by. */
function createGroupRow(row: DatasetRow, cols: string[]): DatasetRow {
  const groupRow: DatasetRow = {};
  cols
    .filter((col) => !!col)
    .forEach((col) => {
      groupRow[col] = row[col];
    });
  return groupRow;
}
